import type { DriverShipment } from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import { Button, Drawer, IconButton } from "@khazzin/ui/primitives";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { useOffline } from "../../offline/Offline";
import { acceptedLines, isPartial } from "./outcome";

/**
 * The Partial picker (DA-10-17, EP10-S17): how many of each item the customer
 * keeps, and what to collect for them — the server's amount, never the
 * phone's (`03/06` §3). The lines came with the day, so the picker works with
 * no signal; the amount then says it is confirmed once back online.
 *
 * Continue, once something is kept and something refused, opens the proof of
 * delivery (EP10-S18) that closes the partial delivery.
 */
export interface PartialPickerProps {
	stop: DriverShipment;
	kept: Record<string, number>;
	onChange: (kept: Record<string, number>) => void;
	onCancel: () => void;
	onContinue: () => void;
}

export function PartialPicker({ stop, kept, onChange, onCancel, onContinue }: PartialPickerProps) {
	const { t } = useI18n();
	// A day cached by an older build has none until the next pull.
	const lines = stop.lines ?? [];

	return (
		<Drawer
			open
			onOpenChange={(open) => {
				if (!open) {
					onCancel();
				}
			}}
			title={t("driver.outcome.partialTitle")}
			description={t("driver.outcome.partialBody")}
			closeLabel={t("driver.outcome.cancel")}
			backLabel={t("driver.outcome.cancel")}
			footer={
				<>
					<Button onClick={onCancel}>{t("driver.outcome.cancel")}</Button>
					<Button
						variant="primary"
						size="lg"
						disabled={lines.length === 0 || !isPartial(lines, kept)}
						onClick={onContinue}
					>
						{t("driver.outcome.continue")}
					</Button>
				</>
			}
		>
			{lines.length === 0 ? (
				<p className="text-[14px] text-fg-muted">{t("driver.outcome.noLines")}</p>
			) : (
				<div className="flex flex-col gap-4">
					<ul className="flex flex-col gap-2">
						{lines.map((line) => {
							const n = kept[line.orderLineId] ?? 0;
							const set = (next: number) => onChange({ ...kept, [line.orderLineId]: next });
							return (
								<li
									key={line.orderLineId}
									className="flex items-center gap-2 rounded-lg border border-border p-3"
								>
									<div className="flex min-w-0 flex-1 flex-col">
										<span className="text-[15px] font-bold text-fg">{line.title}</span>
										<span className="text-[13px] text-fg-muted">{line.sku}</span>
									</div>
									<IconButton
										icon={Minus}
										size="lg"
										label={t("driver.outcome.fewer", { item: line.title })}
										disabled={n === 0}
										onClick={() => set(n - 1)}
									/>
									<output
										aria-label={t("driver.outcome.keptOf", { item: line.title })}
										className="min-w-14 text-center text-[15px] font-bold text-fg"
									>
										{t("driver.outcome.kept", { kept: n, total: line.quantity })}
									</output>
									<IconButton
										icon={Plus}
										size="lg"
										label={t("driver.outcome.more", { item: line.title })}
										disabled={n === line.quantity}
										onClick={() => set(n + 1)}
									/>
								</li>
							);
						})}
					</ul>
					<Collectible stop={stop} kept={kept} />
				</div>
			)}
		</Drawer>
	);
}

/** What to collect: the server's quote for the kept lines. */
function Collectible({ stop, kept }: { stop: DriverShipment; kept: Record<string, number> }) {
	const { t, locale } = useI18n();
	const api = useApi();
	const { online } = useOffline();
	const accepted = acceptedLines(stop.lines, kept);
	const valid = isPartial(stop.lines, kept);
	const quote = useQuery({
		queryKey: ["driver", "collectible", stop.shipmentId, accepted],
		queryFn: () => api.quoteShipmentCollectible(stop.shipmentId, accepted),
		enabled: valid && online && stop.cod !== null,
		retry: false,
	});

	let message: string;
	if (!valid) {
		message = t("driver.outcome.keepSome");
	} else if (stop.cod === null) {
		message = t("driver.outcome.nothingToCollect");
	} else if (!online || quote.isError) {
		message = t("driver.outcome.amountPending");
	} else if (quote.data === undefined) {
		message = t("driver.outcome.amountChecking");
	} else {
		message = t("driver.outcome.collect", {
			amount: formatMoney(
				{
					amountMinor: quote.data.collectibleMinor,
					currency: quote.data.currency ?? stop.cod.currency,
				},
				locale,
			),
		});
	}
	return (
		<p role="status" className="rounded-lg bg-bg-subtle p-3 text-[16px] font-bold text-fg">
			{message}
		</p>
	);
}
